import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services';
import { useUser } from '../../context/UserContext';
import { AdminPageHeader, DataTable, StatTile, SubTabs, Card, Btn, Chip, money, relTime, inputClass } from '../../components/admin/shell';

/**
 * ENGINES › PLAN DETECTION — the scanner's own ledger. The funnel from "the score crossed the
 * line" to "a plan was suggested", what every model read cost, which accounts are running it,
 * and the scorer itself to type at. Nothing here is a word from a chat: decisions, counts,
 * tokens and cents only. View-only by design (the switch lives in SETTINGS).
 */
const OUTCOME_LABEL = {
  plan: 'PLAN', no_plan: 'NO PLAN', rejected: 'REJECTED', error: 'ERROR',
  skipped_no_viewers: 'SKIPPED · NOBODY OPTED IN', skipped_nothing_new: 'SKIPPED · NOTHING NEW', skipped_too_short: 'SKIPPED · TOO SHORT',
};
const OUTCOME_CLASS = { plan: 'text-[#15803D]', error: 'text-[#B91C1C]', rejected: 'text-[#B45309]' };
const outcomeWord = (o) => OUTCOME_LABEL[o] || String(o || '').toUpperCase();
const tokens = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n ?? 0));

export default function PlanDetectionPage() {
  const { can } = useUser();
  const [tab, setTab] = useState('reads');
  const [summary, setSummary] = useState(null);
  useEffect(() => { (async () => { const r = await adminService.getPlanDetection(); if (r.success) setSummary(r.data); })(); }, []);

  const s = summary;
  const meta = s ? (s.enabled ? `${s.model} · reads once a chat is past ${s.threshold} and quiet ${s.quietSeconds}s · ${s.anthropicConfigured ? 'Anthropic key set' : 'NO ANTHROPIC KEY'} · ${s.mapboxConfigured ? 'Mapbox set' : 'no Mapbox token (places stay unmatched)'}` : 'Behind plan_detection_enabled. OFF right now — no chat is read.') : 'Loading…';

  return (
    <>
      <AdminPageHeader section="plan-detection" meta={meta} eyebrowTail={s && !s.enabled && can('settings', 'view') ? <Link to="/admin/settings" className="underline">SETTINGS</Link> : null} />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6" data-testid="plan-tiles">
        <StatTile label="ACCOUNTS OPTED IN" value={s?.optedIn} />
        <StatTile label="HOT CHATS WAITING" value={s?.hotNow} />
        <StatTile label="MODEL READS · 24H" value={s?.today?.calls} />
        <StatTile label="MODEL READS · 30D" value={s?.month?.calls} />
        <StatTile label="COST · 30D" value={s ? money(s.month.costCents) : null} />
        <StatTile label="PLANS FOUND · 30D" value={s ? `${s.month.plans}${s.month.calls ? ` · ${Math.round((s.month.plans / s.month.calls) * 100)}%` : ''}` : null} />
      </div>

      {s && (
        <Card title="THE FUNNEL · LAST 30 DAYS" className="mt-3">
          <div className="grid gap-2 font-mono text-[11px] md:grid-cols-2" data-testid="plan-funnel">
            <div>
              <div><b>{(s.month.calls + s.month.skipped).toLocaleString()}</b> chats went hot and quiet</div>
              <div className="text-[#6B7280]">↳ <b className="text-black">{s.month.skipped.toLocaleString()}</b> skipped before any model call — {s.outcomes30.filter((o) => o.outcome.startsWith('skipped')).map((o) => `${o.count} ${outcomeWord(o.outcome).replace('SKIPPED · ', '').toLowerCase()}`).join(', ') || 'none'}</div>
              <div className="text-[#6B7280]">↳ <b className="text-black">{s.month.calls.toLocaleString()}</b> read by the model · <b className="text-black">{s.month.plans}</b> plans · {s.outcomes30.find((o) => o.outcome === 'no_plan')?.count || 0} no plan · {s.outcomes30.find((o) => o.outcome === 'rejected')?.count || 0} rejected by the rule · {s.outcomes30.find((o) => o.outcome === 'error')?.count || 0} errors</div>
              <div className="text-[#6B7280]">↳ drafts now: <b className="text-black">{s.drafts.suggested}</b> suggested · {s.drafts.dismissed} dismissed · <b className="text-black">{s.drafts.published}</b> published ({s.drafts.publishedPortals} Portals, {s.drafts.publishedEvents} Events)</div>
            </div>
            <div>
              <div>Per read: ~{tokens(s.avgInputTokens)} in · ~{tokens(s.avgOutputTokens)} out · {s.avgDurationMs ? `${(s.avgDurationMs / 1000).toFixed(1)}s` : '—'} · {s.month.calls ? money(Math.round(s.month.costCents / s.month.calls)) : '—'}</div>
              <div className="text-[#6B7280]">24h: {s.today.calls} reads · {money(s.today.costCents)} &nbsp;·&nbsp; 7d: {s.week.calls} reads · {money(s.week.costCents)} &nbsp;·&nbsp; 30d: {tokens(Number(s.month.inputTokens))} in / {tokens(Number(s.month.outputTokens))} out</div>
              <div className="text-[#6B7280]">{s.chatsOff} chats switched off by their members · {s.quietedChats} quieted with × right now</div>
            </div>
          </div>
        </Card>
      )}

      <SubTabs className="mt-4" active={tab} onChange={setTab} tabs={[{ key: 'reads', label: 'READS' }, { key: 'accounts', label: 'ACCOUNTS' }, { key: 'scorer', label: 'SCORER' }]} />
      {tab === 'reads' && <ReadsTab />}
      {tab === 'accounts' && <AccountsTab onPickUser={() => setTab('reads')} />}
      {tab === 'scorer' && <ScorerTab threshold={s?.threshold ?? 0.6} />}
    </>
  );
}

function ReadsTab() {
  const [page, setPage] = useState(1);
  const [callsOnly, setCallsOnly] = useState(true);
  const [outcome, setOutcome] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { setLoading(true); const r = await adminService.getPlanReads({ page, pageSize: 25, outcome, callsOnly: callsOnly && !outcome }); setLoading(false); if (r.success) setData(r.data); })(); }, [page, outcome, callsOnly]);
  const pick = (o) => { setOutcome((cur) => (cur === o ? null : o)); setPage(1); };
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3" data-testid="plan-read-filters">
        <Chip selected={callsOnly && !outcome} onClick={() => { setCallsOnly(true); setOutcome(null); setPage(1); }}>MODEL CALLS</Chip>
        <Chip selected={!callsOnly && !outcome} onClick={() => { setCallsOnly(false); setOutcome(null); setPage(1); }}>EVERY DECISION</Chip>
        {['plan', 'no_plan', 'rejected', 'error', 'skipped_no_viewers'].map((o) => <Chip key={o} selected={outcome === o} onClick={() => pick(o)}>{outcomeWord(o)}</Chip>)}
      </div>
      <DataTable
        columns={[
          { key: 'createdAt', label: 'WHEN', fr: 0.9, render: (r) => relTime(r.createdAt) },
          { key: 'outcome', label: 'OUTCOME', fr: 1.3, render: (r) => <span className={OUTCOME_CLASS[r.outcome] || ''}>{outcomeWord(r.outcome)}{r.reason ? <span className="text-[#9CA3AF]"> · {r.reason}</span> : null}</span> },
          { key: 'source', label: 'HOW', fr: 0.8, render: (r) => (r.source === 'manual' ? `FIND · ${String(r.lookBack || '').toUpperCase()}` : `AUTO · ${Number(r.score).toFixed(2)}`) },
          { key: 'trigger', label: 'ACCOUNT', fr: 1.4, render: (r) => (r.triggerName || r.triggerEmail ? <span title={r.triggerEmail || ''}>{r.triggerName || r.triggerEmail}{r.triggerHandle ? <span className="text-[#9CA3AF]"> @{r.triggerHandle}</span> : null}</span> : '—') },
          { key: 'chat', label: 'CHAT', fr: 1, render: (r) => `${r.threadIsGroup ? 'GROUP' : 'DM'} · ${r.memberCount} IN · ${r.eligibleCount} ON` },
          { key: 'messageCount', label: 'MSGS', fr: 0.5, right: true, render: (r) => (r.messageCount || '—') },
          { key: 'tokens', label: 'TOKENS', fr: 0.9, right: true, render: (r) => (r.inputTokens || r.outputTokens ? `${tokens(r.inputTokens)} / ${tokens(r.outputTokens)}` : '—') },
          { key: 'costCents', label: 'COST', fr: 0.6, right: true, render: (r) => (r.inputTokens ? money(r.costCents) : '—') },
          { key: 'durationMs', label: 'TIME', fr: 0.6, right: true, render: (r) => (r.durationMs ? `${(r.durationMs / 1000).toFixed(1)}s` : '—') },
          { key: 'place', label: 'PLACE', fr: 0.7, render: (r) => (r.placeQueried ? (r.placeMatched ? 'MATCHED' : 'NO MATCH') : '—') },
        ]}
        rows={data?.reads || []}
        loading={loading}
        empty="NO READS YET. THE SCANNER WRITES A ROW FOR EVERY DECISION IT MAKES."
        total={data?.total ?? 0}
        page={page}
        pageSize={25}
        onPage={setPage}
        footerLeft={`${(data?.total ?? 0).toLocaleString()} ${callsOnly && !outcome ? 'model calls' : 'decisions'} · 90 days kept`}
      />
    </>
  );
}

function AccountsTab() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { setLoading(true); const r = await adminService.getPlanAccounts(page, 25); setLoading(false); if (r.success) setData(r.data); })(); }, [page]);
  return (
    <DataTable
      columns={[
        { key: 'name', label: 'ACCOUNT', fr: 1.6, render: (a) => <span title={a.email || ''}>{a.name || a.email || a.userId}{a.handle ? <span className="text-[#9CA3AF]"> @{a.handle}</span> : null}</span> },
        { key: 'enabled', label: 'SUGGEST PLANS', fr: 1, render: (a) => (a.enabled ? <span className="text-[#15803D]">ON · {relTime(a.enabledSince)}</span> : 'OFF') },
        { key: 'chatsOff', label: 'CHATS OFF', fr: 0.6, right: true, render: (a) => a.chatsOff || '—' },
        { key: 'readsEligible', label: 'READS FOR THEM', fr: 0.8, right: true, render: (a) => a.readsEligible || '—' },
        { key: 'manualFinds', label: 'FIND A PLAN', fr: 0.7, right: true, render: (a) => a.manualFinds || '—' },
        { key: 'plansFound', label: 'PLANS', fr: 0.5, right: true, render: (a) => a.plansFound || '—' },
        { key: 'published', label: 'PUBLISHED', fr: 0.7, right: true, render: (a) => a.published || '—' },
        { key: 'costCents', label: 'COST CHARGED', fr: 0.8, right: true, render: (a) => money(a.costCents) },
        { key: 'lastReadAt', label: 'LAST READ', fr: 0.8, render: (a) => relTime(a.lastReadAt) },
      ]}
      rows={data?.accounts || []}
      loading={loading}
      empty="NOBODY HAS TURNED IT ON YET."
      total={data?.total ?? 0}
      page={page}
      pageSize={25}
      onPage={setPage}
      footerLeft={`${(data?.total ?? 0).toLocaleString()} accounts · cost is charged to the account that triggered the read`}
    />
  );
}

function ScorerTab({ threshold }) {
  const [text, setText] = useState("We should do a sunset thing at the lake before it gets cold\nYes! Saturday?\nSaturday works. Paul's Tomb around 6?\nI'm in\nI'll bring the speaker");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); const r = await adminService.scorePlanText(text); setBusy(false); if (r.success) setResult(r.data); };
  return (
    <div className="grid gap-3 p-4 lg:grid-cols-2" data-testid="plan-scorer">
      <Card title="TYPE A CONVERSATION · ONE MESSAGE PER LINE">
        <textarea className={`${inputClass} min-h-[220px]`} value={text} onChange={(e) => setText(e.target.value)} aria-label="Messages to score" />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] text-[#6B7280]">NOTHING IS STORED. THE TEXT NEVER REACHES A MODEL — THIS IS THE REGEX PRE-FILTER ONLY.</span>
          <Btn variant="primary" onClick={run} disabled={busy}>{busy ? 'SCORING…' : 'SCORE IT'}</Btn>
        </div>
      </Card>
      <Card title={`RUNNING SCORE · LINE AT ${threshold}`}>
        {!result && <div className="font-mono text-[11px] text-[#6B7280]">Each message scores 0–1 from seven signal groups (time · day · intent · place · money · bring · size). The chat's running score keeps 72% of the previous value and adds the new one; past {threshold} the chat is hot, and the model reads it once it has been quiet 90 s.</div>}
        {result && (
          <div className="font-mono text-[11px]" data-testid="plan-scorer-result">
            {result.rows.map((r, i) => (
              <div key={i} className={`flex items-start gap-3 border-b border-[#E5E7EB] py-2 ${r.hot ? 'bg-[#F0FDF4]' : ''}`}>
                <span className="w-10 shrink-0 text-right font-bold">{r.running.toFixed(2)}</span>
                <span className="w-12 shrink-0 text-right text-[#6B7280]">+{r.score.toFixed(2)}</span>
                <span className="min-w-0 flex-1 truncate">{r.text}</span>
                <span className="shrink-0 text-[#6B7280]">{r.signals.join(' · ') || '—'}</span>
                {r.hot && <span className="shrink-0 font-bold text-[#15803D]">HOT</span>}
              </div>
            ))}
            <div className="pt-2 font-bold">{result.hot ? 'THIS CHAT WOULD BE READ ONCE IT GOES QUIET.' : 'THIS CHAT WOULD NOT BE READ.'}</div>
          </div>
        )}
      </Card>
    </div>
  );
}
